import $ from 'jquery'
import store from '@/store';

function getFormattedQuantity(value, number_of_decimals = null) {
  if (number_of_decimals === null || isNaN(number_of_decimals)) {
    number_of_decimals = store.getters['settings/getQuantityDecimalPlaces'] ?? 4;
  }
  const parsedVal = parseFloat(value);
  return isNaN(parsedVal) ? 0 : parseFloat(parsedVal.toFixed(number_of_decimals));
}

function getLocalFormattedQuantity(value, number_of_decimals = null) {
  if (number_of_decimals === null || isNaN(number_of_decimals)) {
    number_of_decimals = store.getters['settings/getQuantityDecimalPlaces'] ?? 4;
  }

  return getFormattedQuantity(value, number_of_decimals).toLocaleString('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: number_of_decimals,
  });
}

function getQuantityFormatter(suffix = '', number_of_decimals = null) {
  if (number_of_decimals === null || isNaN(number_of_decimals)) {
    number_of_decimals = store.getters['settings/getQuantityDecimalPlaces'] ?? 4;
  }

  return `#0.${'#'.repeat(number_of_decimals)} ${suffix}`; // `#0.${'0'.repeat(number_of_decimals)}`
}

function roundLotQuantity(value) {
  return Math.round((parseFloat(value) || 0) * 1e6) / 1e6;
}

/**
 * Build a stable key for a lot + expiration_date group.
 */
function getLotGroupKey(lot, expirationDate) {
  const lotKey = lot || '';
  const expKey = expirationDate ? String(expirationDate).slice(0, 10) : '';
  return `${lotKey}|${expKey}`;
}

/**
 * Normalize groups returned by the available-sum endpoint into a common shape
 * used by the lot selector components.
 * Each group: { group_key, lot, expiration_date, available_quantity, measure_unit_id, measure_unit, lot_rows }
 * lot_rows keeps the FIFO order (movement_datetime asc) provided by the backend.
 */
function mapAvailableLotGroups(rawGroups) {
  if (!Array.isArray(rawGroups)) {
    return [];
  }
  return rawGroups.map((group) => {
    const lotRows = Array.isArray(group?.lot_rows) ? group.lot_rows : [];
    return {
      group_key: getLotGroupKey(group?.lot, group?.expiration_date),
      lot: group?.lot ?? null,
      expiration_date: group?.expiration_date ?? null,
      available_quantity: roundLotQuantity(group?.quantity ?? group?.available_quantity ?? 0),
      measure_unit_id: group?.measure_unit_id ?? lotRows[0]?.measure_unit_id ?? null,
      measure_unit: group?.measure_unit ?? lotRows[0]?.measure_unit ?? null,
      unit_price: lotRows[0]?.unit_price ?? null,
      lot_rows: lotRows,
    };
  });
}

/**
 * Split a requested quantity across the underlying stock movement item rows
 * of a lot group (FIFO: rows are consumed in the order provided by the backend).
 * Returns row level lot_items ready for the save payload:
 * [{ stock_movement_item_uuid, quantity, unit_price, measure_unit_id, lot, expiration_date }]
 */
function splitQuantityAcrossLotRows(group, quantity) {
  const rows = Array.isArray(group?.lot_rows) ? group.lot_rows : [];
  let remaining = roundLotQuantity(quantity);
  const result = [];
  for (const row of rows) {
    if (remaining <= 0) {
      break;
    }
    const available = roundLotQuantity(row?.available_quantity);
    if (available <= 0) {
      continue;
    }
    const used = Math.min(available, remaining);
    result.push({
      stock_movement_item_uuid: row.uuid,
      quantity: roundLotQuantity(used),
      unit_price: row.unit_price,
      measure_unit_id: row.measure_unit_id,
      lot: row.lot ?? null,
      expiration_date: row.expiration_date ?? null,
    });
    remaining = roundLotQuantity(remaining - used);
  }
  return result;
}

/**
 * Aggregate saved row-level lot_items (keyed by stock_movement_item_uuid)
 * back into group quantities, using the groups' lot_rows for the mapping.
 * Returns a Map: group_key -> summed quantity.
 */
function aggregateLotItemsByGroup(groups, lotItems) {
  const uuidToGroupKey = new Map();
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    (Array.isArray(group?.lot_rows) ? group.lot_rows : []).forEach((row) => {
      if (row?.uuid) {
        uuidToGroupKey.set(row.uuid, group.group_key);
      }
    });
  });

  const totals = new Map();
  (Array.isArray(lotItems) ? lotItems : []).forEach((item) => {
    const groupKey = uuidToGroupKey.get(item?.stock_movement_item_uuid);
    if (!groupKey) {
      return;
    }
    const current = totals.get(groupKey) || 0;
    totals.set(groupKey, roundLotQuantity(current + (parseFloat(item?.quantity) || 0)));
  });
  return totals;
}


export {
  getFormattedQuantity,
  getLocalFormattedQuantity,
  getQuantityFormatter,
  getLotGroupKey,
  mapAvailableLotGroups,
  splitQuantityAcrossLotRows,
  aggregateLotItemsByGroup
};
